import React, { useEffect, useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import {
  AppDialog,
  Badge,
  Button,
  DataTableCard,
  DataTableCell,
  DataTableRow,
  EmptyState,
  FormActions,
  FormSection,
  LoadingBlock,
} from "../../../shared/ui/composites";
import SchemaField, { isLtrField } from "./SchemaField";
import {
  buildEmptyItem,
  formatCellValue,
  getFieldLabel,
  getSchemaFields,
  inferRowLabel,
  normalizeFieldValue,
  resolveOptions,
  unwrapSettingsRows,
  validateFields,
} from "../utils/schemaForm";
import { useUpdateSettingJsonMutation } from "../api/react-query";
import api from "../../../shared/api/client";
import { fmtInt } from "../../../shared/utils/formatters";
import { scriptForType, isDefaultScript, functionForType, isDefaultFunction } from "../utils/taskScripts";

function getItemId(schema, item, index) {
  const idKey = schema?.item_id || "id";
  return item?.[idKey] ?? index;
}

/**
 * Next sequential integer key (1,2,3…) for array settings items.
 * Looks at schema.item_id (default "id") — also used for sidemenu "key".
 * Non-numeric existing values are ignored when computing max.
 */
function nextSequentialKey(rows, idKey) {
  let max = 0;
  (rows || []).forEach((row) => {
    const raw = row?.[idKey];
    if (raw == null || raw === "") return;
    const n = Number(raw);
    if (Number.isFinite(n) && n > max) max = n;
  });
  return max + 1;
}

function ensureItemKey(item, rows, idKey, editingIndex) {
  const current = item?.[idKey];
  if (current !== "" && current != null) return item;
  const others = (rows || []).filter((_, i) => i !== editingIndex);
  return { ...item, [idKey]: nextSequentialKey(others, idKey) };
}

/** Fill missing keys with 1,2,3… without renaming existing string/numeric keys. */
function fillMissingKeys(rows, idKey) {
  let next = nextSequentialKey(rows, idKey);
  return (rows || []).map((row) => {
    if (row?.[idKey] !== "" && row?.[idKey] != null) return row;
    const filled = { ...row, [idKey]: next };
    next += 1;
    return filled;
  });
}

function formatTaskRunMessage(result) {
  if (!result) return "اجرا شد";
  if (result.skipped) return `اجرا شد (رد شد): ${result.reason || "skipped"}`;
  if (result.fields) {
    return `اجرا شد — پورسانت پرداختی ${fmtInt(result.fields.commission_paid || 0)} ریال، ${fmtInt(result.count ?? result.results?.length ?? 0)} نفر`;
  }
  if (result.paid != null) {
    return `اجرا شد — پورسانت پرداختی ${fmtInt(result.paid)} ریال (${fmtInt(result.count || 0)} نفر)`;
  }
  if (result.enqueued != null) {
    return `اجرا شد — ${fmtInt(result.enqueued)} مورد به صف اضافه شد`;
  }
  if (result.sent != null) {
    return `اجرا شد — ارسال ${fmtInt(result.sent)}، ناموفق ${fmtInt(result.failed || 0)}`;
  }
  try {
    return `اجرا شد: ${JSON.stringify(result).slice(0, 280)}`;
  } catch {
    return "اجرا شد";
  }
}

function applyRelatedFieldChange(field, value, current, fields, allSettings) {
  const next = { ...current, [field.key]: value };

  if (field.key === "type") {
    if (isDefaultScript(current.code, current.type) || !String(current.code || "").trim()) {
      next.code = scriptForType(value);
    }
    if (isDefaultFunction(current.function_code, current.type) || !String(current.function_code || "").trim()) {
      next.function_code = functionForType(value);
    }
    if (value === "personnel_commission" && !next.formula_key) {
      next.formula_key = "personnel_commission";
    }
  }

  if (field.key === "manufacturer_id") {
    const options = resolveOptions(field, allSettings, next);
    const matched = options.find((option) => String(option.value) === String(value));
    next.manufacturer_name =
      matched?.row?.manufacturer_name ||
      (matched && !String(matched.label).startsWith("شناسه") ? matched.label : "") ||
      "";
  }

  if (field.key === "product_group_id") {
    const options = resolveOptions(field, allSettings, next);
    const matched = options.find((option) => String(option.value) === String(value));
    const goodsName =
      Number(value) === -1
        ? "بدون گروه"
        : matched?.row?.GoodsGroupName || matched?.row?.name || matched?.label || "";
    next.product_group_name = goodsName;
    if (!next.name || next.name === current.product_group_name) {
      next.name = goodsName;
    }
  }

  return next;
}

function formatDisplayValue(field, value, allSettings = [], row = {}) {
  if (field?.type === "boolean") {
    return (
      <Badge variant={value ? "job" : "muted"}>
        {value ? "فعال" : "غیرفعال"}
      </Badge>
    );
  }
  if (field?.key === "calendar_type" || field?.key === "unit") {
    const options = resolveOptions(field, allSettings, row);
    const match = options.find((option) => String(option.value) === String(value));
    const label = match?.label || (value != null && value !== "" ? String(value) : "—");
    const gregorian = String(value) === "gregorian";
    return (
      <Badge
        variant={field.key === "calendar_type" ? (gregorian ? "dc" : "job") : "secondary"}
      >
        {label}
      </Badge>
    );
  }
  if (field?.type === "select") {
    const options = resolveOptions(field, allSettings, row);
    const match = options.find((option) => String(option.value) === String(value));
    if (match) return match.label;
    if (Number(value) === -1) return "بدون گروه (-1)";
    if (value !== "" && value != null) return `شناسه ${value}`;
  }
  if (field?.type === "multiselect" || Array.isArray(value)) {
    const items = Array.isArray(value) ? value : [];
    if (!items.length) return <span className="text-slate-400">—</span>;

    let labelMap = null;
    if (field.options_from) {
      const match = String(field.options_from).match(/^settings:([^.]+)\.(.+)$/);
      if (match) {
        const setting = allSettings.find((rowItem) => rowItem.settings_key === match[1]);
        const rows = unwrapSettingsRows(setting);
        labelMap = {};
        rows.forEach((item) => {
          const id = item?.[match[2]];
          if (id == null) return;
          labelMap[String(id)] = inferRowLabel(
            item,
            match[2],
            field.options_label,
            field.options_code,
          );
        });
      }
    }

    return (
      <div className="flex flex-wrap gap-1">
        {items.map((item) => (
          <Badge key={String(item)} variant="secondary">
            {labelMap?.[String(item)] || String(item)}
          </Badge>
        ))}
      </div>
    );
  }

  const text = formatCellValue(value);
  if (isLtrField(field)) {
    return (
      <span dir="ltr" className="inline-block font-mono text-[12px] text-slate-700">
        {text}
      </span>
    );
  }
  return text;
}

export default function ArraySettingEditor({ setting, allSettings = [] }) {
  const schema = setting?.settings_schema || {};
  const fields = useMemo(() => getSchemaFields(schema), [schema]);
  const idKey = schema.item_id || "id";
  const updateMutation = useUpdateSettingJsonMutation();

  const listFields = useMemo(() => {
    const keys = Array.isArray(schema.list_fields) ? schema.list_fields : null;
    if (!keys || !keys.length) return fields;
    const byKey = Object.fromEntries(fields.map((f) => [f.key, f]));
    return keys.map((key) => byKey[key]).filter(Boolean);
  }, [schema.list_fields, fields]);

  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    const raw = setting?.settings_json;
    setRows(Array.isArray(raw) ? raw : unwrapSettingsRows(setting));
    setMessage("");
  }, [setting?.settings_key, setting?.settings_json]);

  const columns = useMemo(
    () => [
      ...listFields.map((field) => ({
        key: field.key,
        label: getFieldLabel(field),
      })),
      { key: "actions", label: "عملیات" },
    ],
    [listFields],
  );

  const openCreate = () => {
    setEditingIndex(null);
    const empty = buildEmptyItem(fields);
    const nextKey = nextSequentialKey(rows, idKey);
    const idField = fields.find((f) => f.key === idKey);
    empty[idKey] = idField?.type === "number" ? nextKey : String(nextKey);
    if (setting?.settings_key === "tasks") {
      empty.type = empty.type || "custom";
      empty.code = empty.code || scriptForType(empty.type);
      empty.function_code = empty.function_code || functionForType(empty.type);
      empty.cron = empty.cron || "0 0 12 * * *";
      empty.timezone = empty.timezone || "Asia/Tehran";
    }
    setFormValues(empty);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (index) => {
    const row = rows[index] || {};
    const next = {};
    fields.forEach((field) => {
      next[field.key] =
        row[field.key] !== undefined
          ? row[field.key]
          : field.default !== undefined
            ? field.default
            : field.type === "boolean"
              ? false
              : field.type === "multiselect" || field.type === "tags" || field.type === "array"
                ? []
                : "";
    });
    // Backfill flat options_* / job_ids from nested options when missing
    if (row.options && typeof row.options === "object") {
      if (
        (!Array.isArray(next.job_ids) || next.job_ids.length === 0) &&
        (Array.isArray(row.options.job_ids) || row.options.job_id != null || row.options_job_id != null)
      ) {
        const ids = Array.isArray(row.options.job_ids)
          ? row.options.job_ids
          : [row.options.job_id ?? row.options_job_id];
        next.job_ids = ids.map(Number).filter(Number.isFinite);
      }
      if (next.timezone === "" || next.timezone == null) {
        if (row.options.timezone) next.timezone = row.options.timezone;
      }
      if (next.options_only_on_failure == null && row.options.only_on_failure != null) {
        next.options_only_on_failure = Boolean(row.options.only_on_failure);
      }
    }
    if (setting?.settings_key === "tasks" && !String(next.code || "").trim()) {
      next.code = scriptForType(next.type);
    }
    if (setting?.settings_key === "tasks" && !String(next.function_code || "").trim()) {
      next.function_code = functionForType(next.type);
    }
    setEditingIndex(index);
    setFormValues(next);
    setErrors({});
    setDialogOpen(true);
  };

  const persistRows = async (nextRows) => {
    const keyedRows = fillMissingKeys(nextRows, idKey);
    try {
      await updateMutation.mutateAsync({
        settingsKey: setting.settings_key,
        settings_json: keyedRows,
      });
      setRows(keyedRows);
      setMessage("ذخیره با موفقیت انجام شد");
      return true;
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || "خطا در ذخیره");
      return false;
    }
  };

  const handleSaveItem = async () => {
    const nextErrors = validateFields(fields, formValues);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const normalizedItem = fields.reduce((acc, field) => {
      acc[field.key] = normalizeFieldValue(field, formValues[field.key]);
      return acc;
    }, {});

    // Keep nested options in sync for runners that still read task.options
    const nestedOptions = {
      ...((editingIndex != null && rows[editingIndex]?.options) || {}),
    };
    if (Array.isArray(normalizedItem.job_ids)) {
      nestedOptions.job_ids = normalizedItem.job_ids
        .map(Number)
        .filter(Number.isFinite);
      if (nestedOptions.job_ids.length) {
        nestedOptions.job_id = nestedOptions.job_ids[0];
      }
    }
    if (normalizedItem.timezone) nestedOptions.timezone = normalizedItem.timezone;
    if (normalizedItem.options_only_on_failure != null) {
      nestedOptions.only_on_failure = Boolean(normalizedItem.options_only_on_failure);
    }
    // Keep nested options.bypass_subscription in sync with the UI boolean
    nestedOptions.bypass_subscription =
      normalizedItem.options_bypass_subscription === true;

    const base =
      editingIndex != null && rows[editingIndex]
        ? { ...rows[editingIndex] }
        : {};
    let item = {
      ...base,
      ...normalizedItem,
      options: nestedOptions,
    };
    // Auto key 1,2,3… for any array JSON setting (sidemenu.key, job.id, …)
    item = ensureItemKey(item, rows, idKey, editingIndex);
    // Coerce to number only when the schema field is typed as number
    const idField = fields.find((f) => f.key === idKey);
    if (idField?.type === "number") {
      const asNum = Number(item[idKey]);
      if (Number.isFinite(asNum)) item[idKey] = asNum;
    } else if (item[idKey] != null && item[idKey] !== "") {
      // Keep string keys as strings (sidemenu.key must stay comparable to job.sidemenu)
      item[idKey] = String(item[idKey]);
    }

    const duplicate = rows.some((row, index) => {
      if (editingIndex != null && index === editingIndex) return false;
      return (
        row?.[idKey] != null &&
        item?.[idKey] != null &&
        String(row[idKey]) === String(item[idKey])
      );
    });

    if (duplicate) {
      setErrors((current) => ({
        ...current,
        [idKey]: `مقدار ${getFieldLabel(fields.find((f) => f.key === idKey) || { key: idKey })} تکراری است`,
      }));
      return;
    }

    const nextRows =
      editingIndex == null
        ? [...rows, item]
        : rows.map((row, index) => (index === editingIndex ? item : row));

    const ok = await persistRows(nextRows);
    if (ok) setDialogOpen(false);
  };

  const handleDelete = async (index) => {
    const item = rows[index];
    const label = item?.name || item?.title || item?.[idKey] || index + 1;
    if (!window.confirm(`آیا از حذف «${label}» مطمئن هستید؟`)) return;
    const nextRows = rows.filter((_, i) => i !== index);
    await persistRows(nextRows);
  };

  const runTaskNow = async (row) => {
    const taskId = row?.[idKey];
    if (!taskId) return;
    try {
      setMessage(`در حال اجرای «${row.title || taskId}»...`);
      const { data } = await api.post(`/pool/tasks/${encodeURIComponent(taskId)}/run`);
      setMessage(formatTaskRunMessage(data?.result));
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || "خطا در اجرای وظیفه");
    }
  };

  const isTasksSetting = setting?.settings_key === "tasks";

  if (!setting) {
    return <LoadingBlock message="در حال بارگذاری..." />;
  }

  if (fields.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        برای این تنظیم، `settings_schema` تعریف نشده است.
      </div>
    );
  }

  return (
    <div>
      <FormSection
        title={setting.title || setting.settings_key}
        className="mb-0 border-0 shadow-none bg-transparent p-0"
      >
        {setting.description && (
          <p className="mb-4 text-sm leading-6 text-slate-500">{setting.description}</p>
        )}

        <FormActions className="mb-4 mt-0 justify-between">
          <Button type="button" variant="submit" onClick={openCreate}>
            افزودن مورد جدید
          </Button>
          {message && (
            <span
              className={cn(
                "text-sm",
                message.includes("موفق") ? "text-emerald-600" : "text-rose-600",
              )}
            >
              {message}
            </span>
          )}
        </FormActions>

        {rows.length === 0 ? (
          <EmptyState
            message="موردی ثبت نشده است. برای شروع یک مورد جدید اضافه کنید."
            actionLabel="افزودن مورد جدید"
            onAction={openCreate}
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <DataTableCard
              columns={columns}
              isEmpty={false}
              minWidth="640px"
              className="border-0 shadow-none rounded-none"
              headerClassName="bg-slate-50"
            >
              {rows.map((row, index) => (
                <DataTableRow key={String(getItemId(schema, row, index))}>
                  {listFields.map((field) => (
                    <DataTableCell key={field.key}>
                      {formatDisplayValue(field, row?.[field.key], allSettings, row)}
                    </DataTableCell>
                  ))}
                  <DataTableCell>
                    <div className="flex flex-wrap gap-2">
                      {isTasksSetting && (
                        <Button
                          type="button"
                          variant="submit"
                          size="sm"
                          onClick={() => runTaskNow(row)}
                        >
                          اجرا الآن
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => openEdit(index)}
                      >
                        ویرایش
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(index)}
                      >
                        حذف
                      </Button>
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableCard>
          </div>
        )}
      </FormSection>

      <AppDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editingIndex == null ? "افزودن مورد" : "ویرایش مورد"}
        size={isTasksSetting ? "xl" : "lg"}
        loading={updateMutation.isPending}
        onConfirm={handleSaveItem}
        confirmLabel={editingIndex == null ? "افزودن" : "ذخیره"}
      >
        <div className="mb-0 grid grid-cols-2 gap-4 max-md:grid-cols-1">
          {fields.map((field) => {
            const isIdField = field.key === idKey;
            const autoOnCreate = editingIndex == null && isIdField;
            return (
              <SchemaField
                key={field.key}
                field={{
                  ...field,
                  // Keep auto key visible but lock it on create so users don't collide
                  editable: autoOnCreate ? false : field.editable !== false,
                  fa: autoOnCreate
                    ? `${getFieldLabel(field)} (خودکار)`
                    : field.fa,
                }}
                value={formValues[field.key]}
                error={errors[field.key]}
                allSettings={allSettings}
                formValues={formValues}
                onChange={(value) =>
                  setFormValues((current) =>
                    applyRelatedFieldChange(field, value, current, fields, allSettings),
                  )
                }
              />
            );
          })}
        </div>
      </AppDialog>
    </div>
  );
}
